'use client';

/**
 * 会话模型选择
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { getThreadModels } from '@/services/thread';

// name 到 img_url 的映射关系类型

type IThreadModel = THREAD.Model;

interface ThreadModelContextType {
  modelList: IThreadModel[];
  modelMap: Map<string, IThreadModel>;
  isLoading: boolean;
  error: Error | null;
  refreshModels: () => Promise<void>;
}

const ThreadModelContext = createContext<ThreadModelContextType | undefined>(
  undefined,
);

export const useThreadModel = () => {
  const context = useContext(ThreadModelContext);
  if (context === undefined) {
    throw new Error('useThreadModel must be used within a ThreadModelProvider');
  }
  return context;
};

interface ThreadModelProviderProps {
  children: ReactNode;
}

export const ThreadModelProvider: React.FC<ThreadModelProviderProps> = ({
  children,
}) => {
  const [modelList, setModelList] = useState<IThreadModel[]>([]);
  const [modelMap, setModelMap] = useState<Map<string, IThreadModel>>(
    new Map(),
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchModels = async () => {
    try {
      setIsLoading(true);
      const modelsData = (await getThreadModels()) || [];
      setModelList(modelsData);

      const modelMap = new Map<string, IThreadModel>();
      modelsData.forEach((model) => {
        modelMap.set(model.model, model);
      });
      setModelMap(modelMap);

      setError(null);
    } catch (err) {
      console.error('Failed to fetch models:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch models'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  const refreshModels = async () => {
    await fetchModels();
  };

  const value = {
    modelList,
    modelMap,
    isLoading,
    error,
    refreshModels,
  };

  return (
    <ThreadModelContext.Provider value={value}>
      {children}
    </ThreadModelContext.Provider>
  );
};
